import {
    ItemRenderProps,
    TreeView,
    TreeViewItemClickEvent,
} from '@progress/kendo-react-treeview';
import { DropdownTrigger } from 'components/dropdown/DropdownTrigger';
import { ButtonLabel, ButtonLabelText } from 'components/dropdown/styled';
import { StatelessPopup } from 'components/popup/stateless_popup/StatelessPopup';
import { useGroupTree } from 'custom_hooks/useAssetTree';
import { usePopup } from 'custom_hooks/usePopup';
import { SimpleTreeItemIcon } from 'pages/workbench/side_panel/workbench_tree/TreeItemGroupIcon';
import { useMemo } from 'react';
import useResizeAware from 'react-resize-aware';
import styled from 'styled-components';
import { GroupTreeItem } from 'utils/tree';

const TreeViewContainer = styled.div`
    background: var(--background-paper-elevation-8);

    .k-treeview {
        background: var(--background-paper-elevation-8);
    }

    .k-treeview-item,
    .k-treeview-leaf {
        background: var(--background-paper-elevation-8);
    }
`;

const ItemContainer = styled.span`
    &:hover {
        background-color: var(--action-hover);
    }
`;

// This type is used because currently these are the only properties used
// by this component, so anything matching this interface can be used
type EntityWithIdAndName = {
    Id: string;
    Name: string;
};

/**
 *
 * @param e onClick event from the TreeViewItem
 * @returns an object with id and name from the onClick event from clicking a TreeView
 */
const getEntityWithIdAndName = (e: TreeViewItemClickEvent) => ({
    Id: e.item.model.id,
    Name: e.item.text,
});

export type GroupDropdownProps = Readonly<{
    /**
     * The currently selected group.
     */
    value: EntityWithIdAndName | undefined;
    /**
     * Callback to call when the selected group should change.
     */
    onChange: (group: EntityWithIdAndName) => void;
    /**
     * ID to put on the button which triggers the dropdown, which
     * can be used for associating a form label.
     */
    id?: string;
    /**
     * CSS class to apply to the dropdown trigger button.
     */
    className?: string;
    /**
     * Whether or not the dropdown button can be triggered.
     */
    disabled?: boolean;
    /**
     * Title to put on the dropdown button.
     */
    title?: string;
    /**
     * The location ID from which groups should be selected.
     */
    locationId: string;
    /**
     * The string to show if no group is selected
     * @default 'Select a group'
     */
    defaultValueText?: string;
    /**
     * if 'undefined' should be given an entry in the list
     */
    allowSelectNone?: boolean;

    /**
     * Whether to include all group types (by default, stops at asset level)
     */
    includeAllGroupTypes?: boolean;

    style?: React.CSSProperties;
}>;

/** only the asset icon should be shown according to the designs for group/asset filtering */
const getAssetIcon = (item: GroupTreeItem) => {
    if (item.model.type === 'Unknown') {
        return <></>;
    }

    return <SimpleTreeItemIcon type={item.model.type} />;
};

const itemRender = (itemToRender: ItemRenderProps) => {
    const groupTreeItem = itemToRender.item as GroupTreeItem;
    return (
        <ItemContainer className="ellipsis-1-line">
            {getAssetIcon(groupTreeItem)}
            <span className="px-5" title={groupTreeItem.model.name}>
                {groupTreeItem.model.name}
            </span>
        </ItemContainer>
    );
};

/**
 * Allows a group (a.k.a. "tree item") to be selected from a list of possible
 * groups under a given location.
 */
export function GroupDropdown({
    id,
    className,
    value,
    onChange,
    disabled,
    title,
    locationId,
    defaultValueText = 'Select a group',
    allowSelectNone,
    includeAllGroupTypes,
}: GroupDropdownProps) {
    const { isOpen, popupRef, triggerRef, toggleOpen, close } = usePopup();
    const [resizeListener, sizes] = useResizeAware();

    const { data, toggleExpanded } = useGroupTree({
        locationId,
        mode: includeAllGroupTypes ? 'all' : 'assetAndAbove',
    });

    const renderData = useMemo(
        () =>
            allowSelectNone
                ? [
                      {
                          model: {
                              type: 'Unknown',
                              name: defaultValueText,
                          },
                      },
                      ...data,
                  ]
                : data,
        [allowSelectNone, data, defaultValueText]
    );

    return (
        <div>
            <DropdownTrigger
                ref={triggerRef}
                onClick={toggleOpen}
                style={{ position: 'relative' }}
                className={className}
                type="button"
                id={id}
                disabled={disabled}
                title={title}
            >
                {resizeListener}
                <ButtonLabel>
                    <ButtonLabelText $isPlaceholder={value === undefined}>
                        {value?.Name ?? defaultValueText}
                    </ButtonLabelText>
                </ButtonLabel>
            </DropdownTrigger>
            {isOpen && (
                <StatelessPopup
                    onClose={close}
                    ref={popupRef}
                    popupClassName="modal-popup menu-max-height"
                    anchor={triggerRef.current}
                    popupSettings={{
                        style: {
                            width: sizes.width ?? 'auto',
                            marginTop: 2,
                            overflowY: 'auto',
                        },
                    }}
                    noOverlay
                >
                    <TreeViewContainer>
                        <TreeView
                            data={renderData}
                            expandIcons
                            onItemClick={(e) => {
                                onChange(getEntityWithIdAndName(e));
                                close();
                            }}
                            item={itemRender}
                            onExpandChange={(e) => toggleExpanded(e.item.model.id)}
                        />
                    </TreeViewContainer>
                </StatelessPopup>
            )}
        </div>
    );
}
